import {
  ExclamationCircleOutlined,
  CheckOutlined,
  CloseOutlined,
  EditOutlined,
  SwapOutlined,
  DeleteOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { Button, message, Modal, Table, Tag, Tooltip } from "antd";
import React, { useState } from "react";
import moment from "moment";
import Progress from "react-progress-2";
import { GetDataAuth } from "../../apiService/GetData";
import { apis } from "../../properties";
import { useRefreshTable } from "../../store";
import useAllFundContents from "../../hooks/useAllFundContents";
import { useNavigate } from "react-router-dom";
import DateUpdateModel from "./DateUpdateModel";
import { FilterRow } from "./FilterRow";

const { confirm } = Modal;

function FundContentsTable() {
  const navigate = useNavigate();
  const setRefreshTable = useRefreshTable((state) => state.setRefreshTable);
  const refreshTable = useRefreshTable((state) => state.refreshTable);
  const [showDateUpdateModel, setShowDateUpdateModel] = useState(false);
  const [selectedId, setSelectedId] = useState();
  const [filterData, setFilterData] = useState({
    page: 1,
    pageSize: 10,
    status: "all",
    lang_id: "en",
    dataTable: true,
    title: "",
    fund_slug: "",
    order: "",
  });

  const allFundContents = useAllFundContents(filterData);

  const columns = [
    {
      title: "Content Order",
      dataIndex: "order_content",
      width: 50,
    },
    {
      title: "Content Title",
      dataIndex: "title",
      width: 200,
    },
    {
      title: "Fund Slug",
      dataIndex: "fund_slug",
      width: 150,
    },
    {
      title: "Created Date",
      render: (record) =>
        record.created_at
          ? moment(record.created_at).format("YYYY-MM-DD h:mm:ss a")
          : "",
      width: 150,
    },
    {
      title: "Language Code",
      dataIndex: "lang_id",
      width: 80,
    },
    {
      title: "Status",
      render: (record) => (
        <Tag
          className="tags"
          color={record.status === 1 ? "#0d8c63" : "#c73b27"}
        >
          {record.status === 1 ? "Active" : "Inactive"}
        </Tag>
      ),
      fixed: "right",
      width: 50,
    },
    {
      title: "Action",
      render: (text, record) => (
        <>
          {record.chart_id && (
            <Tooltip placement="bottom" title="Date Update">
              <Button
                className="view_button"
                shape="circle"
                icon={<CalendarOutlined />}
                onClick={() => {
                  setShowDateUpdateModel(true);
                  setSelectedId(record.id);
                }}
              />
            </Tooltip>
          )}
          &nbsp;&nbsp;
          <Tooltip placement="bottom" title="Edit Fund">
            <Button
              className="view_button"
              shape="circle"
              icon={<EditOutlined />}
              onClick={() => navigate(`/fund-contents/edit/${record.id}`)}
            />
          </Tooltip>
          &nbsp;&nbsp;
          <Tooltip placement="bottom" title="Multi Language">
            <Button
              className="edit_button"
              shape="circle"
              icon={<SwapOutlined />}
              onClick={() =>
                navigate(
                  `/fund-contents/multi-language/${record.id}/${record.fund_content_id}`
                )
              }
            />
          </Tooltip>
          &nbsp;&nbsp;
          {record.status === 1 ? (
            <Tooltip placement="bottom" title="Deactivate Fund">
              <Button
                className="delete_button"
                shape="circle"
                icon={<CloseOutlined />}
                onClick={() => statusChange(record.id, "Inactive")}
              />
            </Tooltip>
          ) : (
            <Tooltip placement="bottom" title="Activate Fund">
              <Button
                className="view_button"
                shape="circle"
                icon={<CheckOutlined />}
                onClick={() => statusChange(record.id, "Active")}
              />
            </Tooltip>
          )}
          &nbsp;&nbsp;
          <Tooltip placement="bottom" title="Delete Fund">
            <Button
              className="delete_button"
              shape="circle"
              icon={<DeleteOutlined />}
              onClick={() => deleteFundContent(record.id)}
            />
          </Tooltip>
        </>
      ),
      fixed: "right",
      width: 120,
      align: "right",
    },
  ];

  const statusChange = (slug, type) => {
    confirm({
      title: `Are you sure?`,
      icon: <ExclamationCircleOutlined />,
      content: `Do  you want to ${type} this fund?`,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk() {
        Progress.show();
        GetDataAuth(`${apis.FUND_CONTENTS_STATUS_CHANGE}/${slug}`).then(
          (result) => {
            let responseJson = result;
            if (responseJson.status === "success") {
              message.success(responseJson.message);
              setRefreshTable(!refreshTable);
              Progress.hide();
            } else if (responseJson.status === "error") {
              message.error(responseJson.message);
              Progress.hide();
            }
          }
        );
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  const deleteFundContent = (id) => {
    confirm({
      title: `Are you sure?`,
      icon: <ExclamationCircleOutlined />,
      content: `Do  you want to delete this content?`,
      okText: "Yes",
      okType: "primary",
      cancelText: "No",
      onOk() {
        Progress.show();
        GetDataAuth(`${apis.FUND_CONTENTS_DELETE}/${id}`).then((result) => {
          let responseJson = result;
          if (responseJson.status === "success") {
            message.success(responseJson.message);
            setRefreshTable(!refreshTable);
            Progress.hide();
          } else if (responseJson.status === "error") {
            message.error(responseJson.message);
            Progress.hide();
          }
        });
      },
      onCancel() {
        console.log("Cancel");
      },
    });
  };

  return (
    <>
      <FilterRow filterData={filterData} setFilterData={setFilterData} />
      <Table
        scroll={{ x: 1500 }}
        columns={columns}
        dataSource={allFundContents.data}
        bordered
        pagination={{
          current: filterData.current_page,
          pageSize: filterData.pageSize,
          total: allFundContents.total,
          onChange: (page, pageSize) => {
            setFilterData({
              ...filterData,
              page: page,
              pageSize: pageSize,
            });
          },
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
      />
      {showDateUpdateModel && (
        <DateUpdateModel
          visible={showDateUpdateModel}
          setVisible={setShowDateUpdateModel}
          selectedId={selectedId}
        />
      )}
    </>
  );
}

export default FundContentsTable;
